import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Typo, HR, Form } from 'tmt-ui-kit';
import moment from 'moment-timezone';
import { connect } from 'react-redux';

import { createBookLink } from '~/utils/payment';
import config from '~/config';
import { convertPriceDetail as convert } from '~/utils/price';
import { createDateArray } from '~/utils/helpers';
import { actions as tripActions } from '~/redux/reducers/trip';
import { actions as sessionActions } from '~/redux/reducers/session';
import { actions as availabilityActions } from '~/redux/reducers/availability';

import BookActionDesktop from '../TripDetail/BookActionDesktop';
import withTrip from './withTrip';
import GroupedTripSelector from './GroupedTripSelector';

const Container = styled.div`
  position: relative;
  padding: 24px 16px;
  p {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const OuterContainer = styled.div`
  position: relative;
`;

const DateContainer = styled.div`
  margin-bottom: 12px;
  :last-child {
    margin-bottom: 0;
  }
`;

const Label = styled(Typo.Medium)`
  margin: 0 !important;
`;

const BetweenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.marginBottom || '8px'};
`;

const Text = styled(Typo.Paragraph)`
  margin: 0 !important;
  padding-bottom: 0;
`;

const Total = styled.span`
  font-size: 16px !important;
  font-weight: 500 !important;
  color: ${props => (props.isMasterTrip ? props.theme.greenLighter : props.theme.orange)} !important;
`;

const priceConverter = (currency, rates) => price => convert(price, currency, rates);

const enhance = connect(
  state => ({
    sessionData: state.session.data,
    rates: state.info.exchangeRates,
    quantity: state.session.data.quantity,
    isSessionDataLoaded: state.session.isSessionDataLoaded,
    availability: state.availability,
  }), {
    setSessionData: sessionActions.setSessionData,
    getTripAvailability: availabilityActions.getTripAvailabilityV2,
    logRequest: tripActions.logMessageCarRental,
  }
);

const RequestBook = ({
  trip,
  sessionData,
  rates,
  quantity,
  setSessionData,
  availability,
  getTripAvailability,
  isSessionDataLoaded,
  logRequest,
}) => {
  const { currency, date: sessionDate } = sessionData;
  const isCalendarLoading = availability.isLoading && isSessionDataLoaded;
  const calendarRef = useRef(null);
  const [endAvailabilities, setEndAvailabilities] = useState([]);
  const [startActiveMonth, setStartActiveMonth] = useState(moment().startOf('day').format('MMMM YYYY'));
  const [endActiveMonth, setEndActiveMonth] = useState(moment().startOf('day').format('MMMM YYYY'));
  const [isShowStartCalendar, setIsShowStartCalendar] = useState(false);
  const [isShowEndCalendar, setIsShowEndCalendar] = useState(false);

  const startDate = moment(sessionDate).startOf('day');
  const endDate = moment(sessionDate).startOf('day').add(quantity - 1, 'days');

  const dateLength = trip.prices.length;
  const fixPath = config.baseURL.en;
  const bookLink = createBookLink(fixPath, 'book', trip, startDate, { quantity }, {}, []);
  const messageLink = 'https://m.me/Takemetour';
  const convertPrice = priceConverter(currency, rates);
  const pricePerDay = convertPrice(trip.prices[quantity - 1]);
  const total = convertPrice(trip.prices[quantity - 1] * quantity);

  const startAvailabilities = trip.is_instant_trip
    ? [
      ...availability.availabilities,
      { date: moment().startOf('day').toISOString(), status: 'n/a' },
      { date: moment().add(1, 'day').startOf('day').toISOString(), status: 'available' }
    ]
    : [{ date: moment().startOf('day').toISOString(), status: 'n/a' }];

  const onChangeQuantity = q => {
    setSessionData({ ...sessionData, quantity: q }, true);
  };

  const onChangeSessionDate = d => {
    setSessionData({ ...sessionData, date: d }, true);
  };

  const changeStartDate = date => {
    setIsShowEndCalendar(true);
    const q = endDate.diff(date, 'days') + 1;
    if (q > trip.prices.length) onChangeQuantity(trip.prices.length);
    else if (q > 0) onChangeQuantity(q);
    onChangeSessionDate(date.toISOString());
  };

  const changeEndDate = date => {
    const q = date.diff(startDate, 'days') + 1;
    if (q > trip.prices.length) onChangeQuantity(trip.prices.length);
    else if (q > 0) onChangeQuantity(q);
  };

  // const handleClickOutside = e => {
  //   console.trace('here', calendarRef.current);
  //   if (calendarRef.current && !calendarRef.current.contains(e.target)) {
  //     console.trace('inhere');
  //     setIsShowStartCalendar(false);
  //     setIsShowEndCalendar(false);
  //   }
  // };

  const handleShowStartCalendar = (value) => {
    if (isShowEndCalendar) setIsShowEndCalendar(false);
    if (trip.is_instant_trip) getTripAvailability(startDate.format('MMMM YYYY'), trip._id);
    setIsShowStartCalendar(value);
  };

  // useEffect(() => {
  //   window.addEventListener('click', handleClickOutside);
  //   return () => {
  //     window.removeEventListener('click', handleClickOutside);
  //   };
  // }, []);

  useEffect(() => {
    const availabilities = [];
    for (
      let date = moment(endActiveMonth).add(-1, 'month').startOf('month');
      date.isBefore(moment(endActiveMonth).add(1, 'month').endOf('month'));
      date.add(1, 'day')
    ) {
      if (!date.isBetween(moment(startDate).subtract(1, 'days'), moment(startDate).add(dateLength, 'days'))) {
        availabilities.push({
          date: date.toISOString(),
          status: 'n/a',
        });
      } else if (trip.is_instant_trip) {
        availabilities.push({
          date: date.toISOString(),
          status: 'available',
        });
      }
    }
    setEndAvailabilities(availabilities);
  }, [sessionDate, endActiveMonth]);

  useEffect(() => {
    if (trip.is_instant_trip) {
      getTripAvailability(moment(startActiveMonth).format('MMMM YYYY'), trip._id);
    }
  }, [startActiveMonth]);

  useEffect(() => {
    if (quantity > dateLength) {
      onChangeQuantity(dateLength);
    }
    if (moment(sessionDate).isBefore(moment())) {
      onChangeSessionDate(moment().add(1, 'day').toISOString());
    }
    if (moment(startDate).endOf('month').isSame(endDate, 'month')) {
      setEndActiveMonth(moment(sessionDate).format('MMMM YYYY'));
    } else {
      setEndActiveMonth(moment(sessionDate).add(1, 'month').format('MMMM YYYY'));
    }
  }, [quantity, sessionDate]);

  const disableActionChangeMonth = { left: false, right: false };
  if (moment(startDate).isSame(moment(startDate).add(7, 'days'), 'month')) {
    disableActionChangeMonth.left = true;
    disableActionChangeMonth.right = true;
  } else if (moment(startDate).add(7, 'days').isSame(moment(endActiveMonth), 'month')) {
    disableActionChangeMonth.right = true;
    disableActionChangeMonth.left = false;
  } else if (moment(startDate).isSame(moment(endActiveMonth), 'month')) {
    disableActionChangeMonth.left = true;
    disableActionChangeMonth.right = false;
  }

  return (
    <OuterContainer>
      <Container>
        {/* Period choosing */}
        <div ref={calendarRef}>
          <GroupedTripSelector />
          <DateContainer>
            <BetweenRow><Label>From</Label></BetweenRow>
            <Form.DatePicker
              currentDate={createDateArray(startDate)}
              onChange={([{ date }]) => changeStartDate(date)}
              tripAvailabilities={startAvailabilities}
              loading={isCalendarLoading}
              onChangeMonthYearCallback={month => setStartActiveMonth(month)}
              isShowCalendar={isShowStartCalendar}
              setIsShowCalendar={handleShowStartCalendar}
              disableClickOutsideToClose
            />
          </DateContainer>
          <DateContainer>
            <BetweenRow><Label>To</Label></BetweenRow>
            <Form.DatePicker
              currentDate={createDateArray(endDate)}
              onChange={([{ date }]) => changeEndDate(date)}
              tripAvailabilities={endAvailabilities}
              onChangeMonthYearCallback={(month) => setEndActiveMonth(month)}
              isShowCalendar={isShowEndCalendar}
              setIsShowCalendar={setIsShowEndCalendar}
              disableActionChangeMonth={disableActionChangeMonth}
              disableClickOutsideToClose
            />
          </DateContainer>
          <HR />
        </div>
        {/* Pricing */}
        <BetweenRow marginBottom="10px">
          <Text>Price per day</Text>
          <Text>{currency} {pricePerDay}</Text>
        </BetweenRow>
        <BetweenRow marginBottom="10px">
          <Text>Total day(s)</Text>
          <Text>{quantity} {quantity > 1 ? 'days' : 'day'}</Text>
        </BetweenRow>
        <BetweenRow marginBottom="10px">
          <Label>Total</Label>
          <Total>{currency} {total}</Total>
        </BetweenRow>
        {/* Buttons */}
        <BookActionDesktop
          trip={trip}
          currency={currency}
          bookLink={bookLink}
          messageLink={messageLink}
          messageLinkProps={{ target: '_blank', rel: 'noopener noreferrer' }}
          onClickMessage={() => logRequest(trip._id, quantity, null, 'message', 'desktop')}
        />
      </Container>
    </OuterContainer>
  );
};

RequestBook.propTypes = {
  trip: PropTypes.object,
  sessionData: PropTypes.object,
  rates: PropTypes.object,
  quantity: PropTypes.number,
  setSessionData: PropTypes.func,
  availability: PropTypes.object,
  getTripAvailability: PropTypes.func,
  isSessionDataLoaded: PropTypes.bool,
  logRequest: PropTypes.func,
};

export default withTrip(enhance(RequestBook));
